import { ActionContext } from 'vuex';
import RootState from '@/store/rootState';
import axios from 'axios';
import Farm from '@/core/interfaces/Farm';
import Barn from '@/core/interfaces/Barn';
import Fence from '@/core/interfaces/Fence';
import Vehicle from '@/core/interfaces/Vehicle';
import AvailableVehicleType from '@/core/interfaces/AvailableVehicleType';
import User from '@/core/interfaces/User';
import VehicleType from '@/core/interfaces/VehicleType';
import VehicleAction from '@/core/interfaces/VehicleAction';
import AvailableVehicleTypeAction from '@/core/interfaces/AvailableVehicleTypeAction';
import Application from '@/core/interfaces/Application';
import Locale from '@/core/interfaces/Locale';
import ArbiterZone from '@/core/interfaces/ArbiterZone';
import TimeZone from '@/core/interfaces/TimeZone';
import Role from '@/core/interfaces/Role';
import UserGroup from '@/core/interfaces/UserGroup';
import FarmGroup from '@/core/interfaces/FarmGroup';
import ZetaHubzone from '@/core/interfaces/ZetaHubzone';
import Module from '@/core/interfaces/Module';
import GroundType from '@/core/interfaces/GroundType';
import CropType from '@/core/interfaces/CropType';

const saveOrUpdate = async <T extends {
    id: number
}>(baseUrl: string, payload: Partial<T>): Promise<T> => {
    let res;
    if (payload.id) {
        res = (await axios.put<T>(`${baseUrl}/${payload.id}`, payload)).data;
    } else {
        res = (await axios.post<T>(baseUrl, payload)).data;
    }

    return res;
};

export const sync = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    await Promise.all([
        context.dispatch('syncApplications'),
        context.dispatch('syncAvailableVehicleTypeActions'),
        context.dispatch('syncAvailableVehicleTypes'),
        context.dispatch('syncBarns'),
        context.dispatch('syncFarms'),
        context.dispatch('syncFarmGroups'),
        context.dispatch('syncFeedTypes'),
        context.dispatch('syncFences'),
        context.dispatch('syncLocales'),
        context.dispatch('syncUsers'),
        context.dispatch('syncUserGroups'),
        context.dispatch('syncRoles'),
        context.dispatch('syncVehicleActions'),
        context.dispatch('syncVehicleTypes'),
        context.dispatch('syncVehicles'),
        context.dispatch('syncArbiterZones'),
        context.dispatch('syncZetaHubZones'),
        context.dispatch('syncTimeZones'),
        context.dispatch('syncModules'),
        context.dispatch('syncGroundTypes'),
        context.dispatch('syncCropTypes'),
        context.dispatch('syncRelayServers'),
    ]);
};

export const syncFarms = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/farms');
    context.commit('setFarms', res.data);
};

export const syncFarmGroups = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/farm-groups');
    context.commit('setFarmGroups', res.data);
};

export const syncBarns = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/barns');
    context.commit('setBarns', res.data);
};

export const syncUsers = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/users');
    context.commit('setUsers', res.data);
};

export const syncUserGroups = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/user-groups');
    context.commit('setUserGroups', res.data);
};

export const syncVehicleTypes = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/vehicle-types');
    context.commit('setVehicleTypes', res.data);
};

export const syncVehicles = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/vehicles');
    context.commit('setVehicles', res.data);
};

export const syncAvailableVehicleTypes = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/farm-available-vehicle-types');
    context.commit('setAvailableVehicleTypes', res.data);
};

export const saveAvailableVehicleType = async(context: ActionContext<RootState, RootState>, payload: Partial<AvailableVehicleType>): Promise<void> => {
    const availableVehicleType = await saveOrUpdate<AvailableVehicleType>('/admin/farm-available-vehicle-types', payload);
    context.commit('saveAvailableVehicleType', availableVehicleType);
};

export const deleteAvailableVehicleType = async(context: ActionContext<RootState, RootState>, payload: number): Promise<void> => {
    const res = await axios.delete(`/admin/farm-available-vehicle-types/${payload}`);
    context.commit('removeAvailableVehicleType', res.data);
};
export const syncFences = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/fences');
    context.commit('setFences', res.data);
};

export const syncFeedTypes = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/feed-types');
    context.commit('setFeedTypes', res.data);
};

export const deleteFarm = async(context: ActionContext<RootState, RootState>, farmId: number): Promise<void> => {
    await axios.delete(`/admin/farms/${farmId}`);

    context.commit('removeFarm', farmId);
};

export const saveFarm = async(context: ActionContext<RootState, RootState>, payload: Partial<Farm>): Promise<Farm> => {
    const farm = await saveOrUpdate<Farm>('/admin/farms', payload);

    context.commit('saveFarm', farm);

    return farm;
};

export const saveBarn = async(context: ActionContext<RootState, RootState>, payload: Partial<Barn>): Promise<Barn> => {
    const barn = await saveOrUpdate<Barn>('/admin/barns', payload);

    context.commit('saveBarn', barn);

    return barn;
};

export const deleteBarn = async(context: ActionContext<RootState, RootState>, barnId: number): Promise<void> => {
    await axios.delete(`/admin/barns/${barnId}`);

    context.commit('removeBarn', barnId);
};

export const saveFence = async(context: ActionContext<RootState, RootState>, payload: Partial<Fence>): Promise<Fence> => {
    const fence = await saveOrUpdate<Fence>('/admin/fences', payload);

    context.commit('saveFence', fence);

    return fence;
};

export const deleteFence = async(context: ActionContext<RootState, RootState>, fenceId: number): Promise<void> => {
    await axios.delete(`/admin/fences/${fenceId}`);

    context.commit('removeFence', fenceId);
};

export const saveVehicle = async(context: ActionContext<RootState, RootState>, payload: Partial<Vehicle>): Promise<Vehicle> => {
    const vehicle = await saveOrUpdate<Vehicle>('/admin/vehicles', payload);

    context.commit('saveVehicle', vehicle);

    return vehicle;
};

export const deleteVehicle = async(context: ActionContext<RootState, RootState>, vehicleId: number): Promise<void> => {
    await axios.delete(`/admin/vehicles/${vehicleId}`);

    context.commit('removeVehicle', vehicleId);
};

export const saveUser = async(context: ActionContext<RootState, RootState>, payload: Partial<User>): Promise<User> => {
    const user = await saveOrUpdate<User>('/admin/users', payload);

    context.commit('saveUser', user);

    return user;
};

export const deleteUser = async(context: ActionContext<RootState, RootState>, userId: number): Promise<void> => {
    await axios.delete(`/admin/users/${userId}`);

    context.commit('removeUser', userId);
};

export const saveVehicleType = async(context: ActionContext<RootState, RootState>, payload: Partial<VehicleType>): Promise<VehicleType> => {
    const vehicleType = await saveOrUpdate<VehicleType>('/admin/vehicle-types', payload);

    context.commit('saveVehicleType', vehicleType);

    return vehicleType;
};

export const deleteVehicleType = async(context: ActionContext<RootState, RootState>, vehicleTypeId: number): Promise<void> => {
    await axios.delete(`/admin/vehicle-types/${vehicleTypeId}`);

    context.commit('removeVehicleType', vehicleTypeId);
};

export const syncVehicleActions = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/vehicle-actions');
    context.commit('setVehicleActions', res.data);
};

export const syncAvailableVehicleTypeActions = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get('/admin/vehicle-type-actions');
    context.commit('setAvailableVehicleTypeActions', res.data);
};

export const deleteAvailableVehicleTypeAction = async(context: ActionContext<RootState, RootState>, vehicleTypeActionId: number): Promise<void> => {
    await axios.delete(`/admin/vehicle-type-actions/${vehicleTypeActionId}`);

    context.commit('removeAvailableVehicleTypeAction', vehicleTypeActionId);
};

export const saveAvailableVehicleTypeAction = async(
    context: ActionContext<RootState, RootState>,
    payload: Partial<AvailableVehicleTypeAction>,
): Promise<void> => {
    const res = await axios.post('/admin/vehicle-type-actions', payload);
    context.commit('saveAvailableVehicleTypeAction', res.data);
};

export const saveVehicleAction = async(context: ActionContext<RootState, RootState>, payload: Partial<VehicleAction>): Promise<VehicleAction> => {
    const vehicleAction = await saveOrUpdate<VehicleAction>('/admin/vehicle-actions', payload);

    context.commit('saveVehicleAction', vehicleAction);

    return vehicleAction;
};

export const deleteVehicleAction = async(context: ActionContext<RootState, RootState>, vehicleActionId: number): Promise<void> => {
    await axios.delete(`/admin/vehicle-actions/${vehicleActionId}`);

    context.commit('removeVehicleAction', vehicleActionId);
};

export const syncApplications = async(context: ActionContext<any, any>): Promise<void> => {
    const res = await axios.get('/admin/applications');
    context.commit('setApplications', res.data);
};

export const saveApplication = async(context: ActionContext<RootState, RootState>, payload: Partial<Application>): Promise<Application> => {
    const application = await saveOrUpdate<Application>('/admin/applications', payload);

    context.commit('saveApplication', application);

    return application;
};

export const deleteApplication = async(context: ActionContext<RootState, RootState>, applicationId: number): Promise<void> => {
    await axios.delete(`/admin/applications/${applicationId}`);

    context.commit('removeApplication', applicationId);
};

export const syncLocales = async(context: ActionContext<any, any>): Promise<void> => {
    const res = await axios.get('/admin/locales');
    context.commit('setLocales', res.data);
};

export const saveLocale = async(context: ActionContext<RootState, RootState>, payload: Partial<Locale>): Promise<Locale> => {
    const locale = await saveOrUpdate<Locale>('/admin/locales', payload);

    context.commit('saveLocale', locale);

    return locale;
};

export const deleteLocale = async(context: ActionContext<RootState, RootState>, localeId: number): Promise<void> => {
    await axios.delete(`/admin/locales/${localeId}`);

    context.commit('removeLocale', localeId);
};

export const syncArbiterZones = async(context: ActionContext<any, any>): Promise<void> => {
    const res = await axios.get('/admin/arbiter-zones');

    context.commit('setArbiterZones', res.data);
};

export const saveArbiterZone = async(context: ActionContext<RootState, RootState>, payload: Partial<ArbiterZone>): Promise<ArbiterZone> => {
    const arbiterZone = await saveOrUpdate<ArbiterZone>('/admin/arbiter-zones', payload);

    context.commit('saveArbiterZone', payload.id ? payload : arbiterZone);

    return arbiterZone;
};

export const deleteArbiterZone = async(context: ActionContext<RootState, RootState>, arbiterZoneId: number): Promise<void> => {
    await axios.delete(`/admin/arbiter-zones/${arbiterZoneId}`);

    context.commit('removeArbiterZone', arbiterZoneId);
};

export const syncZetaHubZones = async(context: ActionContext<any, any>): Promise<void> => {
    const res = await axios.get('/admin/zeta-hub-zones');

    context.commit('setZetaHubZones', res.data);
};

export const saveZetaHubZone = async(context: ActionContext<RootState, RootState>, payload: Partial<ZetaHubzone>): Promise<ZetaHubzone> => {
    const zetaHubZone = await saveOrUpdate<ZetaHubzone>('/admin/zeta-hub-zones', payload);

    context.commit('saveZetaHubZone', payload.id ? payload : zetaHubZone);

    return zetaHubZone;
};

export const deleteZetaHubZone = async(context: ActionContext<RootState, RootState>, zetaHubZoneId: number): Promise<void> => {
    await axios.delete(`/admin/zeta-hub-zones/${zetaHubZoneId}`);

    context.commit('removeZetaHubZone', zetaHubZoneId);
};

export const syncTimeZones = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get<TimeZone[]>('/admin/time-zones');
    context.commit('setTimeZones', res.data);
};

export const syncRoles = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get<Role[]>('/admin/roles');
    context.commit('setRoles', res.data);
};

export const deleteUserGroup = async(context: ActionContext<RootState, RootState>, userGroupId: number): Promise<void> => {
    await axios.delete(`/admin/user-groups/${userGroupId}`);

    context.commit('removeUserGroup', userGroupId);
};

export const saveUserGroup = async(context: ActionContext<RootState, RootState>, payload: Partial<UserGroup>): Promise<UserGroup> => {
    const userGroup = await saveOrUpdate<UserGroup>('/admin/user-groups', payload);

    context.commit('saveUserGroup', userGroup);

    return userGroup;
};

export const deleteFarmGroup = async(context: ActionContext<RootState, RootState>, farmGroupId: number): Promise<void> => {
    await axios.delete(`/admin/farm-groups/${farmGroupId}`);

    context.commit('removeFarmGroup', farmGroupId);
};

export const saveFarmGroup = async(context: ActionContext<RootState, RootState>, payload: Partial<FarmGroup>): Promise<FarmGroup> => {
    const farmGroup = await saveOrUpdate<FarmGroup>('/admin/farm-groups', payload);

    context.commit('saveFarmGroup', farmGroup);

    return farmGroup;
};

export const deleteRole = async(context: ActionContext<RootState, RootState>, roleId: number): Promise<void> => {
    await axios.delete(`/admin/roles/${roleId}`);

    context.commit('removeRole', roleId);
};

export const saveRole = async(context: ActionContext<RootState, RootState>, payload: Partial<Role>): Promise<Role> => {
    const role = await saveOrUpdate<Role>('/admin/roles', payload);

    context.commit('saveRole', role);

    return role;
};

const toggleMembership = <T extends { id: number }>(
    groups: T[],
    getIds: (group: T) => number[] | undefined,
    memberId: number,
    groupIds: number[],
): { group: T, ids: number[] }[] => groups
        .filter(group => (getIds(group) || []).includes(memberId) !== groupIds.includes(group.id))
        .map(group => ({
            group,
            ids: groupIds.includes(group.id)
                ? [...getIds(group) || [], memberId]
                : (getIds(group) || []).filter(id => id !== memberId),
        }));

export const saveUserGroupsByUser = async(context: ActionContext<RootState, RootState>, payload: {
    userId: number,
    userGroupIds: number[]
}): Promise<void> => {
    const changed = toggleMembership(context.state.userGroups, userGroup => userGroup.user_ids, payload.userId, payload.userGroupIds);

    await Promise.all(changed.map(({ group, ids }) => context.dispatch('saveUserGroup', {
        ...group,
        user_ids: ids,
    })));

    await context.dispatch('syncUserGroups');
};

export const saveFarmGroupsByFarm = async(context: ActionContext<RootState, RootState>, payload: {
    farmId: number,
    farmGroupIds: number[]
}): Promise<void> => {
    const changed = toggleMembership(context.state.farmGroups, farmGroup => farmGroup.farm_ids, payload.farmId, payload.farmGroupIds);

    await Promise.all(changed.map(({ group, ids }) => context.dispatch('saveFarmGroup', {
        ...group,
        farm_ids: ids,
    })));

    await context.dispatch('syncFarmGroups');
};

export const syncModules = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get<Module[]>('/admin/farms/modules');
    context.commit('setModules', res.data);
};

export const syncGroundTypes = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get<GroundType[]>('/admin/grass-calendar/ground-types');
    context.commit('setGroundTypes', res.data);
};

export const saveGroundType = async(context: ActionContext<RootState, RootState>, payload: Partial<GroundType>): Promise<GroundType> => {
    const groundType = await saveOrUpdate<GroundType>('/admin/grass-calendar/ground-types', payload);

    context.commit('saveGroundType', groundType);

    return groundType;
};

export const deleteGroundType = async(context: ActionContext<RootState, RootState>, groundTypeId: number): Promise<void> => {
    await axios.delete(`/admin/grass-calendar/ground-types/${groundTypeId}`);

    context.commit('removeGroundType', groundTypeId);
};

export const syncCropTypes = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const res = await axios.get<CropType[]>('/admin/grass-calendar/crop-types');
    context.commit('setCropTypes', res.data);
};

export const saveCropType = async(context: ActionContext<RootState, RootState>, payload: Partial<CropType>): Promise<CropType> => {
    const cropType = await saveOrUpdate<CropType>('/admin/grass-calendar/crop-types', payload);

    context.commit('saveCropType', cropType);

    return cropType;
};

export const deleteCropType = async(context: ActionContext<RootState, RootState>, cropTypeId: number): Promise<void> => {
    await axios.delete(`/admin/grass-calendar/crop-types/${cropTypeId}`);

    context.commit('removeCropType', cropTypeId);
};

export const syncRelayServers = async(context: ActionContext<RootState, RootState>): Promise<void> => {
    const response = await axios.get('/admin/relay-servers');

    context.commit('setRelayServers', response.data);
};
